export const PROOF_STATUS_VALUES = [
  'pending',
  'not_approved',
  'approved',
] as const

export type ProofStatus = (typeof PROOF_STATUS_VALUES)[number]

export type ProofEntry = {
  proof_id?: string
  url: string
  status: ProofStatus
  created_at?: string
  customer_comment?: string | null
}

/** Nested shape from `quote` → `quote_proof` → `proof` selects. */
export type QuoteProofLinkRow = {
  proof_id: string
  status: string
  sort_order: number
  linked_at: string
  customer_comment?: string | null
  proof: { id: string; url: string; created_at: string } | null
}

export function proofEntriesFromQuoteProofLinks(
  links: QuoteProofLinkRow[] | null | undefined
): ProofEntry[] {
  if (!links?.length) return []
  const sorted = [...links].sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order
    const ta = new Date(a.linked_at || 0).getTime()
    const tb = new Date(b.linked_at || 0).getTime()
    if (ta !== tb) return ta - tb
    const ca = new Date(a.proof?.created_at || 0).getTime()
    const cb = new Date(b.proof?.created_at || 0).getTime()
    return ca - cb
  })
  const out: ProofEntry[] = []
  for (const row of sorted) {
    const url = row.proof?.url
    if (!url) continue
    const status = isProofStatus(row.status) ? row.status : 'pending'
    const created_at = row.proof?.created_at
    out.push({
      proof_id: row.proof_id,
      url,
      status,
      created_at: created_at || undefined,
      customer_comment: row.customer_comment ?? null,
    })
  }
  return out
}

function isProofStatus(v: unknown): v is ProofStatus {
  return (
    typeof v === 'string' &&
    (PROOF_STATUS_VALUES as readonly string[]).includes(v)
  )
}

export function getFilenameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname
    const segments = pathname.split('/').filter(Boolean)
    return segments[segments.length - 1] || 'file'
  } catch {
    return 'file'
  }
}

export function isPdfUrl(url: string): boolean {
  return getFilenameFromUrl(url).toLowerCase().endsWith('.pdf')
}
